// src/helpers/storeLocations.ts
//
// Keeps the store locator's list in sync with the official store list for the
// widget's country — US, or MX for the Spanish brands (served via the widget
// API's /stores/locations, see ezquote-backend services/storeLocations.js) —
// without ever making the customer wait: the last known list (or the bundled
// location.json on first load) is shown immediately, and a background check
// fetches only the stores that differ from the bundled file (location.json for
// US, location-mx.json for MX). Any failure leaves the current list (or the
// bundled file) in place.

import { CACHE_CONSTANTS } from "../../constants/cacheConstants";
import DataRequest from "../../services/dataRequest";
import { HTTP_METHODS } from "../../eums/httpRequestsMethods";
import type { Store } from "../../components/ez-quote/ez-quote.types";
import type { StoreCountry } from "../../locales";

// Each country's list is cached separately; US keeps the original key.
const storeLocationsKey = (country: StoreCountry) =>
  country === "US" ? CACHE_CONSTANTS.CACHE_KEYS.STORE_LOCATIONS : `${CACHE_CONSTANTS.CACHE_KEYS.STORE_LOCATIONS}_${country.toLowerCase()}`;

export type StoreLocations = {
  version: string | null;
  // `updatedAt` of the bundled location.json these stores were built on.
  baseline: string | null;
  stores: Store[];
  // When the official lists were last checked successfully (ms since epoch).
  checkedAt?: number;
};

// Fields compared to decide whether a store's details changed.
const STORE_FIELDS: (keyof Store)[] = [
  "name", "brandName", "addressLine1", "addressLine2", "city", "stateAlternateText",
  "zipCode", "countryName", "phoneNumber", "latitude", "longitude", "deptId",
];

const isSameStore = (a: Store, b: Store) =>
  STORE_FIELDS.every((field) => (a[field] ?? "") === (b[field] ?? ""));

/**
 * Adds new stores and updates changed ones (matched by id). Stores missing from
 * `incoming` are kept — a store already shown never disappears. Returns the
 * original `base` array when nothing changed, so callers can skip re-renders.
 */
export const mergeStores = (base: Store[], incoming: Store[]): { stores: Store[]; changed: boolean } => {
  const byId = new Map(base.map((store) => [store.id, store]));
  let changed = false;

  incoming.forEach((store) => {
    // Without a deptId a quote can't be sent to the store.
    if (!store?.id || !store.deptId) return;
    const existing = byId.get(store.id);
    if (!existing || !isSameStore(existing, store)) {
      byId.set(store.id, existing ? { ...existing, ...store } : store);
      changed = true;
    }
  });

  return changed ? { stores: Array.from(byId.values()), changed } : { stores: base, changed };
};

export const readCachedStoreLocations = (country: StoreCountry = "US"): StoreLocations | null => {
  try {
    const raw = localStorage.getItem(storeLocationsKey(country));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed?.stores) && parsed.stores.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedStoreLocations = (country: StoreCountry, storeLocations: StoreLocations) => {
  try {
    localStorage.setItem(storeLocationsKey(country), JSON.stringify(storeLocations));
  } catch {
    // Storage full/blocked — the in-memory list is still used for this visit.
  }
};

const loadBundledStoreLocations = async (country: StoreCountry): Promise<StoreLocations> => {
  // Separate chunks, so a widget only downloads its own country's file.
  const bundled = country === "MX" ? await import("../location-mx.json") : await import("../location.json");
  return { version: null, baseline: bundled?.updatedAt ?? null, stores: (bundled?.data ?? []) as Store[] };
};

/**
 * The list to show right away: the last synced list, or the bundled file
 * the first time (and whenever storage is unavailable).
 */
export const loadInitialStoreLocations = async (country: StoreCountry = "US"): Promise<StoreLocations> =>
  readCachedStoreLocations(country) ?? loadBundledStoreLocations(country);

const isCheckDue = (checkedAt?: number) =>
  !checkedAt || Date.now() - checkedAt >= CACHE_CONSTANTS.STORE_LOCATIONS_SYNC_INTERVAL;

// Resolves with null if the request takes too long; a late response is ignored.
const withTimeout = <T,>(request: Promise<T>): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CACHE_CONSTANTS.STORE_LOCATIONS_SYNC_TIMEOUT);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Background check against the official lists, at most once a day. Sends the
 * bundled file's id and the last synced version, and receives only the stores
 * that differ from the bundled file (or nothing when the version is unchanged).
 * Resolves with the merged list when something was added or changed, or null
 * when there is nothing to apply (checked within the last day, nothing new, or
 * the lists couldn't be reached within the timeout — the current list stays
 * and a failed check is retried on the next load).
 */
export const syncStoreLocations = async (current: StoreLocations, country: StoreCountry = "US"): Promise<Store[] | null> => {
  if (!isCheckDue(current.checkedAt)) return null;

  try {
    // Usually `current` already is the bundled file; after a release with a
    // newer location.json, the newer file is loaded (from the browser cache).
    const bundled = current.version === null ? current : await loadBundledStoreLocations(country);
    const params: Record<string, string> = { country };
    if (bundled.baseline) params.baseline = bundled.baseline;
    if (current.version) params.since = current.version;

    const response: any = await withTimeout(
      DataRequest.execute("stores/locations", { method: HTTP_METHODS.GET, params }, {}, false)
    );
    const payload = response?.data?.success ? response.data.payload : null;
    if (!payload?.version || !Array.isArray(payload.stores)) return null;
    const checkedAt = Date.now();

    if (payload.version === current.version) {
      writeCachedStoreLocations(country, { ...current, baseline: bundled.baseline, checkedAt });
      return null;
    }

    // A diff is relative to the bundled file, so a list synced on an older
    // file first takes the newer file's stores, then the diff on top. A full
    // list (payload.full) needs no rebase.
    const needsRebase = !payload.full && current.baseline !== bundled.baseline;
    const base = needsRebase ? mergeStores(current.stores, bundled.stores) : { stores: current.stores, changed: false };
    const merged = mergeStores(base.stores, payload.stores);

    writeCachedStoreLocations(country, { version: payload.version, baseline: bundled.baseline, stores: merged.stores, checkedAt });
    return base.changed || merged.changed ? merged.stores : null;
  } catch {
    return null;
  }
};

/**
 * Shows the initial list, runs the background check, and checks again when
 * the customer comes back to a tab left open for a day or more (an open tab
 * never reloads, so the on-load check alone would never run again). Calls
 * `onStores` with the list to show. Returns a cleanup function.
 */
export const watchStoreLocations = (
  onStores: (stores: Store[]) => void,
  country: StoreCountry = "US"
): (() => void) => {
  let stopped = false;
  let syncing = false;
  let lastAttemptAt = 0;

  const sync = async (current: StoreLocations) => {
    if (syncing) return;
    syncing = true;
    lastAttemptAt = Date.now();
    try {
      const updatedStores = await syncStoreLocations(current, country);
      if (!stopped && updatedStores) onStores(updatedStores);
    } finally {
      syncing = false;
    }
  };

  loadInitialStoreLocations(country).then((initial) => {
    if (stopped) return;
    onStores(initial.stores);
    sync(initial);
  });

  // At most one attempt a day from an open tab, even after a failed check.
  const onVisibilityChange = () => {
    if (document.visibilityState !== "visible" || syncing) return;
    if (Date.now() - lastAttemptAt < CACHE_CONSTANTS.STORE_LOCATIONS_SYNC_INTERVAL) return;
    loadInitialStoreLocations(country).then((current) => {
      if (!stopped) sync(current);
    });
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
